// Task blocks: the lines indented below a task line form that task's body (docs/api.md v0.3).
// Pure analysis (unit-testable) + a CodeMirror fold service for task lines.
import { foldService } from "@codemirror/language";
import type { EditorState, Text } from "@codemirror/state";
import { parseTaskLine } from "./tasktokens";

/** Visual horizontal offset as `em + px` (spaces scale with the font, the checkbox is fixed). */
export interface VisualX {
  em: number;
  px: number;
}

export interface OpenTask {
  /** 1-based line number of the task line. */
  line: number;
  /** Leading whitespace length of the task line. */
  indent: number;
  /** Content column: bullet indent + marker + 1 (e.g. 2 for "- [ ]" at indent 0). */
  contentCol: number;
  /** Visual x of the task line's bullet / checkbox. */
  vis: VisualX;
}

export interface LineInfo {
  /** Enclosing task blocks, outermost first (empty when the line is not inside a task body). */
  enclosing: OpenTask[];
  /** Leading whitespace length. */
  indent: number;
  /** The line is itself a task line (outside code fences). */
  isTask: boolean;
  blank: boolean;
}

export interface TaskBlockAnalysis {
  /** Per line (index = line number - 1). */
  lines: LineInfo[];
  /** Task line number → last line number of its block (only tasks with a non-empty block). */
  blocks: Map<number, number>;
}

/** Width of the rendered checkbox incl. its margin in Rich mode (see .cm-lp-task). */
export const CHECKBOX_PX = 21;
/** Width of one indentation space in Rich mode inside task blocks. */
export const SPACE_EM = 0.5;

function leadingWs(text: string): number {
  let i = 0;
  while (i < text.length && (text[i] === " " || text[i] === "\t")) i++;
  return i;
}

/** Visual x of column `col` on a line whose enclosing tasks are `stack` (outermost first). */
export function visualX(col: number, stack: OpenTask[]): VisualX {
  const t = stack[stack.length - 1];
  if (!t || col <= t.indent) {
    if (t) {
      // Should not happen for enclosed lines (col > t.indent); fall back to the parent chain.
      return visualX(col, stack.slice(0, -1));
    }
    return { em: col * SPACE_EM, px: 0 };
  }
  if (col >= t.contentCol) {
    return { em: t.vis.em + (col - t.contentCol) * SPACE_EM, px: t.vis.px + CHECKBOX_PX };
  }
  const frac = (col - t.indent) / Math.max(1, t.contentCol - t.indent);
  return { em: t.vis.em, px: t.vis.px + Math.round(CHECKBOX_PX * frac) };
}

/**
 * Analyse task blocks: a task line's block is the following lines indented deeper than the task's
 * bullet (blank lines included), ending before the next non-blank line with indent ≤ the task's
 * indent. Trailing blank lines are not part of the block. Fenced code is never a task.
 */
export function analyzeTaskBlocks(lines: readonly string[]): TaskBlockAnalysis {
  const info: LineInfo[] = new Array(lines.length);
  const blocks = new Map<number, number>();
  let stack: (OpenTask & { last: number })[] = [];
  let pendingBlank: number[] = [];
  let fence: string | null = null;

  const close = (t: OpenTask & { last: number }) => {
    if (t.last > t.line) blocks.set(t.line, t.last);
  };

  for (let i = 0; i < lines.length; i++) {
    const text = lines[i];
    const lineNo = i + 1;
    const indent = leadingWs(text);
    const blank = indent === text.length;
    if (blank) {
      pendingBlank.push(i);
      info[i] = { enclosing: [], indent, isTask: false, blank: true };
      continue;
    }
    // Pop tasks whose block ends before this line.
    while (stack.length && stack[stack.length - 1].indent >= indent) close(stack.pop()!);
    // Blank lines between belong to the still-open blocks.
    const enclosing = stack.map(({ last: _l, ...t }) => t);
    for (const b of pendingBlank) info[b] = { enclosing, indent: lines[b].length, isTask: false, blank: true };
    pendingBlank = [];
    for (const t of stack) t.last = lineNo;

    const trimmed = text.slice(indent);
    let isTask = false;
    if (fence !== null) {
      if (trimmed.startsWith(fence)) fence = null;
    } else {
      const m = /^(`{3,}|~{3,})/.exec(trimmed);
      if (m) fence = m[1];
      else {
        const task = parseTaskLine(text);
        if (task) {
          isTask = true;
          const contentCol = indent + task.marker.length + 1;
          stack.push({ line: lineNo, indent, contentCol, vis: visualX(indent, enclosing), last: lineNo });
        }
      }
    }
    info[i] = { enclosing, indent, isTask, blank: false };
  }
  // Trailing blank lines are outside every block.
  for (const b of pendingBlank) info[b] = { enclosing: [], indent: lines[b].length, isTask: false, blank: true };
  while (stack.length) close(stack.pop()!);
  return { lines: info, blocks };
}

const cache = new WeakMap<Text, TaskBlockAnalysis>();

/** Memoised analysis of an editor document. */
export function taskBlocksOf(doc: Text): TaskBlockAnalysis {
  let a = cache.get(doc);
  if (!a) {
    const lines: string[] = [];
    for (let it = doc.iterLines(); !it.next().done; ) lines.push(it.value);
    a = analyzeTaskBlocks(lines);
    cache.set(doc, a);
  }
  return a;
}

/** Fold range for a task line with a block: end of the task line → end of its last block line. */
export function taskFoldRange(state: EditorState, lineStart: number): { from: number; to: number } | null {
  const line = state.doc.lineAt(lineStart);
  const end = taskBlocksOf(state.doc).blocks.get(line.number);
  if (!end) return null;
  return { from: line.to, to: state.doc.line(end).to };
}

export const taskFoldService = foldService.of((state, lineStart) => taskFoldRange(state, lineStart));
